/**
 * SetVersionTagModal Component
 *
 * Modal for setting a version tag on a deployment.
 * Note: Version tags are immutable once set.
 */

import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { setVersionTagFormSchema, type SetVersionTagFormValidation } from '../../../lib/validators/deployment';
import type { DeploymentSanitized } from '../../../types/deployment.types';
import { useSetVersionTag } from '../hooks/useSetVersionTag';
import { getErrorMessage } from '../../../lib/utils/errors';

interface SetVersionTagModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  applicationId: string;
  deployment: DeploymentSanitized;
  onSuccess?: () => void;
}

/**
 * SetVersionTagModal component
 */
export const SetVersionTagModal: React.FC<SetVersionTagModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  applicationId,
  deployment,
  onSuccess,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<SetVersionTagFormValidation>({
    resolver: zodResolver(setVersionTagFormSchema),
    defaultValues: {
      versionTag: '',
    },
  });

  const setVersionTagMutation = useSetVersionTag({
    tenantId,
    applicationId,
    onSuccess: () => {
      onSuccess?.();
      handleClose();
    },
  });

  const handleClose = () => {
    if (!setVersionTagMutation.isPending) {
      reset();
      setVersionTagMutation.reset();
      onClose();
    }
  };

  const onSubmit = (data: SetVersionTagFormValidation) => {
    setVersionTagMutation.mutate({
      deploymentId: deployment.deploymentId,
      versionTag: data.versionTag,
    });
  };

  const isLoading = setVersionTagMutation.isPending;
  const error = setVersionTagMutation.error;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>Set Version Tag</Modal.Header>

      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Error Alert */}
          {error && (
            <Alert variant="error">
              {getErrorMessage(error)}
            </Alert>
          )}

          {/* Warning about immutability */}
          <Alert variant="warning">
            <div className="space-y-2">
              <p className="font-semibold">Version tags cannot be changed!</p>
              <p className="text-sm">
                Once set, the version tag is immutable and will permanently identify this deployment.
                Make sure you use the correct semantic version.
              </p>
            </div>
          </Alert>

          {/* Deployment Info */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div>
              <span className="text-sm font-medium text-gray-700">Deployment:</span>
              <p className="text-gray-900 font-mono text-sm">
                {deployment.deploymentId.slice(0, 8)}...
              </p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Version:</span>
              <p className="text-gray-900">{deployment.version}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Status:</span>
              <p className="text-gray-900 capitalize">{deployment.deploymentStatus}</p>
            </div>
          </div>

          {/* Version Tag Field */}
          <div>
            <Input
              label="Version Tag"
              placeholder="v1.0.0"
              {...register('versionTag')}
              error={errors.versionTag?.message}
              disabled={isLoading}
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Use semantic versioning format (e.g., v1.0.0 or 1.0.0)
            </p>
          </div>
        </form>
      </Modal.Body>

      <Modal.Footer>
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit(onSubmit)}
            loading={isLoading}
            disabled={isLoading}
          >
            Set Version Tag
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
